import type { ComponentProps } from "react";
import { cn } from "../../lib/cn";

const variants = {
  /** Sits on the page ground: surface fill with a hairline edge. */
  surface: "bg-surface border border-edge",
  /** One step up: raised fill, elevation carried by shadow. */
  raised: "bg-raised shadow-md",
  /** Quiet tonal panel for grouping, no edge. */
  muted: "bg-muted-bg",
} as const;

const paddings = {
  none: "",
  sm: "p-4",
  md: "p-5",
  lg: "p-6",
} as const;

export interface CardProps extends ComponentProps<"div"> {
  variant?: keyof typeof variants;
  padding?: keyof typeof paddings;
}

export function Card({ variant = "surface", padding = "md", className, ...props }: CardProps) {
  return (
    <div
      className={cn("rounded-xl text-content", variants[variant], paddings[padding], className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("mb-4 flex items-start justify-between gap-4", className)} {...props} />
  );
}

export interface CardTitleProps extends ComponentProps<"h3"> {
  /**
   * The heading level, which is about the page's outline rather than the look: a card directly
   * under the page's `h1` is an `h2`, one inside a section is an `h3`. Levels must not skip.
   */
  as?: "h2" | "h3" | "h4";
}

export function CardTitle({ as: Heading = "h3", className, children, ...props }: CardTitleProps) {
  return (
    <Heading className={cn("font-display font-semibold text-base text-heading", className)} {...props}>
      {children}
    </Heading>
  );
}

export function CardDescription({ className, ...props }: ComponentProps<"p">) {
  return <p className={cn("mt-1 text-sm text-subtle", className)} {...props} />;
}

export function CardContent({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("text-sm", className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "mt-5 flex items-center justify-end gap-3 border-t border-edge-subtle pt-4",
        className,
      )}
      {...props}
    />
  );
}
